import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/Slice/authSlice";
import { can } from "../pages/admin/permissions";
import { useAlerts } from "./Alerts";
import { askDue, markAsked, notificationsSupported } from "../utils/pushNotifications";
import { getNewOrders } from "../services/admin";
import { acceptOrder, rejectOrder } from "../services/orders";
import { errorMessage } from "../services";
import { money } from "../utils/format";
import { toast as notify } from "../utils/toast";
import { ordersChanged, onOrdersChanged } from "../utils/ordersChanged";
import "./new-order-alarm.css";

const CHECK_EVERY = 15 * 1000; // looks for new orders every 15 seconds (and at once on a live order alert)
const RING_FOR = 20 * 1000; // each ring lasts 20 seconds
const REPEAT_EVERY = 10 * 60 * 1000; // rings again every 10 minutes until every order is answered
const SNOOZE_KEY = "ownezzy_order_alarm_snooze";
// Which orders already rang and when it last rang, kept in the browser: reloading the page or opening another tab
// must not ring again for the same orders, and every tab shares the 10-minute clock
const RANG_KEY = "ownezzy_order_alarm_rang";
const REMEMBER = 300; // the most recent orders kept

// An order is known by its website and number together: two websites can both have an order #ORD0000001
const keyOf = (order) => `${order.site}:${order.orderId}`;

// A chat with the shopper about their order (the phone they gave at checkout); "" without a usable number
const whatsappLink = (order) => {
  const digits = String(order.phone || "").replace(/\D/g, "");
  const number = digits.length === 10 ? `91${digits}` : digits.length >= 11 && digits.length <= 15 ? digits : "";
  if (!number) return "";
  const text = `Hello ${order.customer}, this is ${order.siteName}. Thank you for your order #${order.orderId}.`;
  return `https://wa.me/${number}?text=${encodeURIComponent(text)}`;
};

const readSnooze = () => {
  try { return Number(localStorage.getItem(SNOOZE_KEY)) || 0; } catch { return 0; }
};
const writeSnooze = (until) => {
  try { localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* snoozed for this page only */ }
};
// { ids: [order keys that rang], at: when it last rang }. Without storage the alarm remembers for this page only.
let rangHere = { ids: [], at: 0 };
const readRang = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(RANG_KEY) || "null");
    if (saved && Array.isArray(saved.ids)) return { ids: saved.ids, at: Number(saved.at) || 0 };
  } catch { /* use this page's memory */ }
  return rangHere;
};
const writeRang = (rang) => {
  rangHere = { ids: rang.ids.slice(-REMEMBER), at: rang.at };
  try { localStorage.setItem(RANG_KEY, JSON.stringify(rangHere)); } catch { /* this page only */ }
};

// The ring: a phone-like double beep, made in the browser (no sound file). Browsers only play sound after the
// page has been clicked once, so the audio is unlocked on the first click.
let audio = null;
const audioContext = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audio) audio = new Ctx();
  return audio;
};
if (typeof window !== "undefined") {
  const unlock = () => { const ctx = audioContext(); if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {}); };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}
function ring(seconds) {
  const ctx = audioContext();
  if (!ctx) return () => {};
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  const gain = ctx.createGain();
  gain.connect(ctx.destination);
  const start = ctx.currentTime + 0.05;
  const oscillators = [];
  // Every 1.5 seconds: two short beeps
  for (let t = 0; t < seconds; t += 1.5) {
    [0, 0.25].forEach((offset) => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 880;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, start + t + offset);
      g.gain.linearRampToValueAtTime(0.35, start + t + offset + 0.02);
      g.gain.setValueAtTime(0.35, start + t + offset + 0.18);
      g.gain.linearRampToValueAtTime(0, start + t + offset + 0.2);
      osc.connect(g);
      g.connect(gain);
      osc.start(start + t + offset);
      osc.stop(start + t + offset + 0.22);
      oscillators.push(osc);
    });
  }
  return () => {
    oscillators.forEach((osc) => { try { osc.stop(); } catch { /* already stopped */ } });
    gain.disconnect();
  };
}

// Store owners (and staff who handle orders): a new order rings for 20 seconds and shows here until it is accepted or
// rejected. Every waiting order shares one 10-minute clock: after a ring it rings again only 10 minutes later (✕ or
// Snooze also quietens it for 10 minutes), except that an order it has never rung for rings at once.
export default function NewOrderAlarm() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [snoozedUntil, setSnoozedUntil] = useState(readSnooze);
  const [ringing, setRinging] = useState(false);
  const [busy, setBusy] = useState(""); // the order key being answered, or "all"
  const [rejecting, setRejecting] = useState(null); // { key, reason }: one order, or key "all" for every order
  const stopSound = useRef(null);
  const ringTimer = useRef(null);
  // The orders this tab has rung for. It starts from the ones already rung (so a reload does not ring for them
  // again), but each tab keeps its own: a tab in the background, whose sound the browser may keep muted, can no
  // longer mark a new order as rung for the tab in front, which then stayed silent.
  const heard = useRef(null);
  if (heard.current === null) heard.current = new Set(readRang().ids);
  const [now, setNow] = useState(Date.now());

  const check = useCallback(() => getNewOrders().then(({ data }) => setOrders(data.orders)).catch(() => {}), []);
  useEffect(() => {
    check();
    const timer = setInterval(() => { check(); setNow(Date.now()); }, CHECK_EVERY);
    // A new order (live alert), an order answered elsewhere, or coming back to the tab: look again at once
    const stopListening = onOrdersChanged(check);
    const onVisible = () => { if (!document.hidden) { check(); setNow(Date.now()); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      stopListening();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  const silence = useCallback(() => {
    clearTimeout(ringTimer.current);
    if (stopSound.current) stopSound.current();
    stopSound.current = null;
    setRinging(false);
  }, []);

  // Rings for an order it never rang for (even when snoozed), or again 10 minutes after the last ring while not snoozed
  useEffect(() => {
    if (!orders.length) { silence(); return; }
    const rang = readRang();
    const keys = orders.map(keyOf);
    const fresh = keys.some((key) => !heard.current.has(key));
    const due = now >= snoozedUntil && Date.now() - rang.at >= REPEAT_EVERY;
    if (!fresh && !due) return;
    keys.forEach((key) => heard.current.add(key));
    writeRang({ ids: [...rang.ids.filter((key) => !keys.includes(key)), ...keys], at: Date.now() });
    if (fresh) { setSnoozedUntil(0); writeSnooze(0); }
    silence();
    stopSound.current = ring(RING_FOR / 1000);
    setRinging(true);
    // In another tab: the 10-minute reminder also shows as a notification (a new order already has one, from Alerts.js
    // or the service worker), and it stays until it is clicked
    if (!fresh && document.hidden && notificationsSupported() && Notification.permission === "granted") {
      try {
        const note = new Notification(`🔔 ${orders.length} new order${orders.length === 1 ? "" : "s"} waiting`, {
          body: "Accept or reject them in Ownezzy.", tag: "order-alarm", renotify: true, requireInteraction: true, icon: "/brand/icon-192.png",
        });
        note.onclick = () => { window.focus(); note.close(); };
      } catch { /* this browser only shows notifications from the service worker */ }
    }
    ringTimer.current = setTimeout(silence, RING_FOR);
  }, [orders, now, snoozedUntil, silence]);
  useEffect(() => () => silence(), [silence]);

  // ✕ and Snooze: quiet for 10 minutes, for every waiting order together
  const snooze = () => {
    const until = Date.now() + REPEAT_EVERY;
    setSnoozedUntil(until);
    writeSnooze(until);
    writeRang({ ...readRang(), at: Date.now() });
    setRejecting(null);
    silence();
  };

  const send = (order, accept, reason) => (accept ? acceptOrder(order.site, order.orderId) : rejectOrder(order.site, order.orderId, reason));

  const answer = async (order, accept, reason = "") => {
    setBusy(keyOf(order));
    try {
      const { data } = await send(order, accept, reason);
      notify(data.message);
      setRejecting(null);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy("");
      ordersChanged(); // Manage orders and the badges show the new status, and this alarm checks again
    }
  };

  // Accept all / Reject all: one order after another; any that fail (e.g. answered meanwhile) are counted
  const answerAll = async (accept, reason = "") => {
    setBusy("all");
    const list = [...orders];
    let failed = 0;
    for (const order of list) {
      // eslint-disable-next-line no-await-in-loop -- one at a time, so stock is returned in order
      await send(order, accept, reason).catch(() => { failed += 1; });
    }
    const done = list.length - failed;
    notify(`${done} order${done === 1 ? "" : "s"} ${accept ? "accepted" : "rejected"}${failed ? `, ${failed} could not be` : ""}`, failed ? "error" : undefined);
    setRejecting(null);
    setBusy("");
    ordersChanged();
  };

  const view = (o) => navigate(`/admin/sites/${o.site}/orders?order=${encodeURIComponent(o.orderId)}`);

  if (!orders.length) return null;
  const snoozed = snoozedUntil > Date.now();
  if (snoozed) {
    return (
      <button className="order-alarm-mini" onClick={() => { setSnoozedUntil(0); writeSnooze(0); }} aria-label="Show new orders">
        <span className="order-alarm-bell" aria-hidden="true">🔔</span>
        {orders.length} new order{orders.length === 1 ? "" : "s"} waiting
      </button>
    );
  }

  const several = orders.length > 1;
  const rejectingAll = rejecting && rejecting.key === "all";
  return (
    <div className="order-alarm" role="alertdialog" aria-label="New orders">
      <button className="order-alarm-close" onClick={snooze} aria-label="Close and snooze for 10 minutes" title="Snooze for 10 minutes">✕</button>
      <div className="order-alarm-head">
        <span className={`order-alarm-bell ${ringing ? "is-ringing" : ""}`} aria-hidden="true">🔔</span>
        <div>
          <b>{several ? `${orders.length} new orders!` : "New order!"}</b>
          <small>Accept or reject {several ? "each one" : "it"}. It rings every 10 minutes until you do.</small>
        </div>
      </div>
      {several && (rejectingAll ? (
        <div className="order-alarm-reject order-alarm-all">
          <input value={rejecting.reason} maxLength={300} placeholder="Reason for all (optional), e.g. shop closed today" autoFocus
            onChange={(e) => setRejecting({ key: "all", reason: e.target.value })} aria-label="Reason for rejecting every order" />
          <button className="order-alarm-no" disabled={busy === "all"} onClick={() => answerAll(false, rejecting.reason)}>Reject all {orders.length}</button>
          <button className="order-alarm-link" onClick={() => setRejecting(null)}>Back</button>
        </div>
      ) : (
        <div className="order-alarm-actions order-alarm-all">
          <button className="order-alarm-yes" disabled={Boolean(busy)} onClick={() => answerAll(true)}>✓ Accept all</button>
          <button className="order-alarm-no" disabled={Boolean(busy)} onClick={() => setRejecting({ key: "all", reason: "" })}>✕ Reject all</button>
        </div>
      ))}
      <ul className="order-alarm-list">
        {orders.slice(0, 4).map((o) => {
          const key = keyOf(o);
          return (
            <li key={key}>
              <div className="order-alarm-info">
                <b>#{o.orderId} · {money(o.total)}</b>
                <small>{o.siteName} · {o.customer} · {o.items} item{o.items === 1 ? "" : "s"} · {o.payment}</small>
              </div>
              {rejecting && rejecting.key === key ? (
                <div className="order-alarm-reject">
                  <input value={rejecting.reason} maxLength={300} placeholder="Reason (optional), e.g. out of stock" autoFocus
                    onChange={(e) => setRejecting({ key, reason: e.target.value })} aria-label="Reason for rejecting" />
                  <button className="order-alarm-no" disabled={busy === key} onClick={() => answer(o, false, rejecting.reason)}>Reject order</button>
                  <button className="order-alarm-link" onClick={() => setRejecting(null)}>Back</button>
                </div>
              ) : (
                <div className="order-alarm-actions">
                  <button className="order-alarm-view" onClick={() => view(o)}>View order</button>
                  <button className="order-alarm-yes" disabled={Boolean(busy)} onClick={() => answer(o, true)}>✓ Accept</button>
                  <button className="order-alarm-no" disabled={Boolean(busy)} onClick={() => setRejecting({ key, reason: "" })}>✕ Reject</button>
                  {whatsappLink(o) && <a className="order-alarm-wa" href={whatsappLink(o)} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${o.customer}`}>WhatsApp</a>}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {orders.length > 4 && <p className="order-alarm-more">+{orders.length - 4} more: answer these first, use Accept all, or open Order management.</p>}
      <button className="order-alarm-snooze" onClick={snooze}>🔕 Snooze 10 minutes</button>
    </div>
  );
}

// Store owners, and staff who handle orders, on every page of the admin panel: the alarm, and while notifications are
// not allowed, a request to allow them so orders ring in other tabs and with the panel closed too. After Later, or a
// no to the browser's question, it comes back a day later (checked every hour while the panel stays open).
export function GlobalOrderAlarm() {
  const user = useSelector(selectUser);
  const { pathname } = useLocation();
  const alerts = useAlerts();
  const [due, setDue] = useState(askDue);
  useEffect(() => {
    const timer = setInterval(() => setDue(askDue()), 60 * 60 * 1000);
    return () => clearInterval(timer);
  }, []);
  const handlesOrders = user && !user.mustChangePassword && (user.role === "storeadmin" || (user.role === "staff" && can(user, "orders")));
  const permission = alerts && alerts.permission;
  // Not asked yet (e.g. a new owner who came straight from sign-up, never clicking Sign in): the browser's question
  // comes with the first click or key press anywhere in the panel, since browsers only ask after one
  const turnOn = alerts && alerts.turnOn;
  useEffect(() => {
    if (!handlesOrders || !turnOn || permission !== "default" || !due || !notificationsSupported()) return undefined;
    const ask = () => { markAsked(); setDue(false); turnOn(); };
    window.addEventListener("pointerdown", ask, { once: true });
    window.addEventListener("keydown", ask, { once: true });
    return () => { window.removeEventListener("pointerdown", ask); window.removeEventListener("keydown", ask); };
  }, [handlesOrders, turnOn, permission, due]);
  if (!handlesOrders || !pathname.startsWith("/admin")) return null;

  const later = () => { markAsked(); setDue(false); };
  const allow = async () => {
    await alerts.turnOn();
    setDue(askDue()); // asked just now: a no hides it for a day
  };
  const ask = alerts && notificationsSupported() && due && (permission === "default" || permission === "denied");
  const blocked = permission === "denied";
  return (
    <>
      <NewOrderAlarm />
      {ask && (
        <div className="notify-prompt" role="dialog" aria-label="Allow notifications">
          <span className="notify-prompt-bell" aria-hidden="true">🔔</span>
          <div>
            <b>Never miss an order</b>
            {blocked ? (
              <small>Notifications are blocked, so new orders can't ring in another tab or when Ownezzy is closed. To allow them, tap the 🔒 icon next to the website address, set Notifications to Allow, and reload the page.</small>
            ) : (
              <small>Allow notifications so new orders ring even in another tab or when Ownezzy is closed.</small>
            )}
          </div>
          <div className="notify-prompt-actions">
            {blocked
              ? <button className="order-alarm-yes" onClick={() => window.location.reload()}>I've allowed them</button>
              : <button className="order-alarm-yes" onClick={allow}>Allow</button>}
            <button className="notify-prompt-later" onClick={later}>Later</button>
          </div>
        </div>
      )}
    </>
  );
}
